import { Head, useForm, Link } from '@inertiajs/react'
import AuthLayout from '../../components/AuthLayout'
import ClientAuthLayout from '../../components/ClientAuthLayout'

interface Props {
  errors: {
    password?: string
    password_confirmation?: string
    reset_password_token?: string
    base?: string
  }
  reset_password_token: string
  csrf_token?: string
  client_flow?: boolean
}

export default function ResetPassword({ errors, reset_password_token, csrf_token, client_flow = false }: Props) {
  const { data, setData, put, processing, transform } = useForm({
    account: {
      password: '',
      password_confirmation: '',
      reset_password_token: reset_password_token,
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Devise signs the account in right after a successful reset, so this PUT
    // has the same session-persistence requirement as login. Send the fresh
    // CSRF token explicitly rather than trusting the XSRF-TOKEN cookie.
    transform((current) => ({ ...current, authenticity_token: csrf_token ?? '' }))
    put(client_flow ? '/clients/password' : '/users/password')
  }

  if (client_flow) {
    return (
      <>
        <Head title="Reset Password - Pinnacle Navigator" />
        <ClientAuthLayout>
          {errors.base && (
            <div className="mb-6 rounded border-l-[3px] border-[#C7161E] bg-[#F9E8E8] p-3" role="alert">
              <p className="text-base text-text-primary">{errors.base}</p>
            </div>
          )}
          {errors.reset_password_token && (
            <div className="mb-6 rounded border-l-[3px] border-[#C7161E] bg-[#F9E8E8] p-3" role="alert">
              <p className="text-base text-text-primary">
                Reset token {errors.reset_password_token}. Please <Link href="/clients/password/new" className="font-medium underline underline-offset-4">request a new one</Link>.
              </p>
            </div>
          )}
          <div className="mb-8 text-center">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-text-primary">Client access</p>
            <h1 className="font-serif text-3xl italic text-text-primary">Set your new password</h1>
            <p className="mt-3 text-sm leading-6 text-text-primary">Choose a strong password for your account.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <input type="hidden" name="account[reset_password_token]" value={reset_password_token} />
            <div>
              <label htmlFor="client-password" className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-text-primary">New password</label>
              <input id="client-password" type="password" value={data.account.password} onChange={e => setData('account', { ...data.account, password: e.target.value })} className="input w-full" placeholder="Enter new password" required autoComplete="new-password" autoFocus minLength={6} />
              {errors.password && <p className="mt-1 text-sm text-text-primary">{errors.password}</p>}
            </div>
            <div>
              <label htmlFor="client-password-confirmation" className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-text-primary">Confirm password</label>
              <input id="client-password-confirmation" type="password" value={data.account.password_confirmation} onChange={e => setData('account', { ...data.account, password_confirmation: e.target.value })} className="input w-full" placeholder="Confirm new password" required autoComplete="new-password" minLength={6} />
              {errors.password_confirmation && <p className="mt-1 text-sm text-text-primary">{errors.password_confirmation}</p>}
            </div>
            <button type="submit" disabled={processing} className="w-full rounded-button bg-pinnacle-red px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-pinnacle-red-dark disabled:cursor-not-allowed disabled:opacity-70">
              {processing ? 'Resetting...' : 'Reset password'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <Link href="/clients/sign_in" className="text-base font-medium text-text-primary underline underline-offset-4 hover:text-[#C7161E]">Back to sign in</Link>
          </div>
        </ClientAuthLayout>
      </>
    )
  }

  return (
    <>
      <Head title="Reset Password - Pinnacle" />
      <AuthLayout>
        {errors.base && (
          <div
            className="mb-6 p-3 rounded"
            style={{
              background: 'rgba(237, 28, 36, 0.08)',
              borderLeft: '3px solid #ED1C24',
            }}
          >
            <p className="font-sans text-base text-text-primary">{errors.base}</p>
          </div>
        )}

        {errors.reset_password_token && (
          <div
            className="mb-6 p-3 rounded"
            style={{
              background: 'rgba(237, 28, 36, 0.08)',
              borderLeft: '3px solid #ED1C24',
            }}
          >
            <p className="font-sans text-base text-text-primary">
              Reset token {errors.reset_password_token}. Please <Link href="/users/password/new" className="text-text-primary hover:underline">request a new one</Link>.
            </p>
          </div>
        )}

        <p className="font-serif italic text-2xl text-text-primary text-center mb-2"
           style={{ color: '#1B1B1B' }}>
          Set your new password
        </p>
        <p className="font-sans text-sm text-text-muted text-center mb-8">
          Choose a strong password for your account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <input type="hidden" name="account[reset_password_token]" value={reset_password_token} />

          <div>
            <label htmlFor="password" className="block font-sans text-xs font-bold uppercase tracking-[0.1em] text-text-primary mb-2">
              New Password
            </label>
            <input
              id="password"
              type="password"
              value={data.account.password}
              onChange={e => setData('account', { ...data.account, password: e.target.value })}
              className="input w-full"
              placeholder="Enter new password"
              required
              autoComplete="new-password"
              autoFocus
              minLength={6}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-[#ED1C24]">{errors.password}</p>
            )}
          </div>

          <div>
            <label htmlFor="password_confirmation" className="block font-sans text-xs font-bold uppercase tracking-[0.1em] text-text-primary mb-2">
              Confirm Password
            </label>
            <input
              id="password_confirmation"
              type="password"
              value={data.account.password_confirmation}
              onChange={e => setData('account', { ...data.account, password_confirmation: e.target.value })}
              className="input w-full"
              placeholder="Confirm new password"
              required
              autoComplete="new-password"
              minLength={6}
            />
            {errors.password_confirmation && (
              <p className="mt-1 text-sm text-[#ED1C24]">{errors.password_confirmation}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={processing}
            className="w-full bg-pinnacle-red hover:bg-pinnacle-red-dark text-white font-bold text-sm uppercase tracking-wider py-3.5 px-6 rounded-button transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {processing ? 'RESETTING...' : 'RESET PASSWORD'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link href="/users/sign_in" className="text-base font-sans text-text-primary hover:underline font-medium">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    </>
  )
}
